import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

// Hooks
import { useCardListing } from '../hooks';

// Services
import { createLocalProductCardsRepository } from '../services';

// Selectors
import { selectIsDeletable, selectListingPageCount } from '../selectors';

// Types
import { TableSortState } from 'types/DesignSystem/Table';
import { CardListingRow, ProductCard, ProductCardsRepository } from '../types';

interface ProductCardsContextValue {
  /** The CURRENT PAGE of the listing — the server's answer to the live query. */
  rows: CardListingRow[];
  /** Matching cards across all pages: the pager's denominator. */
  total: number;
  isLoading: boolean;
  searchTerm: string;
  /**
   * The typed term has outrun the query — the debounce is still running, so the
   * rows on screen answer an older term. `isLoading` cannot say this: it starts
   * with the request, which is the debounce late (§ 288).
   */
  isSearchPending: boolean;
  setSearchTerm: (searchTerm: string) => void;
  showCount: number;
  setShowCount: (showCount: number) => void;
  /** 1-based listing page, clamped when the server's total shrinks under it. */
  page: number;
  setPage: (page: number) => void;
  sortState: TableSortState;
  setSort: (sortState: TableSortState) => void;
  hasNoSearchMatches: boolean;
  /** Creates a card with default configuration; null when the write failed. */
  /**
   * Creates a card, or REJECTS with the server's refusal — a duplicate name
   * arrives as a `ProductCardFieldError` naming the input. The caller renders
   * it; swallowing it here is what made a refused create look like a dead
   * button.
   */
  createCard: (name: string) => Promise<ProductCard | null>;
  /** Removes a card — refuses while any application still references it (R25). */
  deleteCard: (cardId: string) => Promise<void>;
  refresh: () => void;
}

const ProductCardsContext = createContext<ProductCardsContextValue | null>(null);

interface ProductCardsProviderProps {
  /**
   * The persistence port. Defaults to the browser-storage adapter; tests and
   * the future HTTP adapter pass their own without any UI change (ADR-001).
   */
  repository?: ProductCardsRepository;
  children: React.ReactNode;
}

export const ProductCardsProvider = ({ repository, children }: ProductCardsProviderProps) => {
  // One adapter instance for the provider's lifetime, so a re-render does not
  // hand the effects a new identity every pass.
  const defaultRepository = useMemo(() => createLocalProductCardsRepository(), []);
  const activeRepository = repository ?? defaultRepository;

  const listing = useCardListing();
  const {
    searchTerm,
    isSearchPending,
    setSearchTerm,
    showCount,
    setShowCount,
    page,
    setPage,
    sortState,
    setSort,
    query,
  } = listing;

  const [rows, setRows] = useState<CardListingRow[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [reloadToken, setReloadToken] = useState(0);

  // Every listing interaction is a repository query (operator decision
  // 2026-08-19): the query object changing IS the fetch trigger.
  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    activeRepository
      .list(query)
      .then((result) => {
        if (cancelled) {
          return;
        }
        setRows(result.rows);
        setTotal(result.total);
        // The reader's page can outlive its rows — a delete emptied it, or
        // another session shrank the listing. Land on the new last page,
        // never a blank one; the setPage re-triggers this effect once.
        const pageCount = selectListingPageCount(result.total, query.limit);
        if (query.page > pageCount) {
          setPage(pageCount);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setRows([]);
          setTotal(0);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [activeRepository, query, setPage, reloadToken]);

  const refresh = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  const createCard = useCallback(
    async (name: string): Promise<ProductCard | null> => {
      // Deliberately NOT caught here. It used to be, and a create that the
      // server refused — a duplicate name, since 2026-09-21 — left the dialog
      // open reporting nothing at all: the user pressed Create, watched it do
      // nothing, and pressed it again. The caller owns the message, because the
      // caller owns the box the name was typed into.
      const created = await activeRepository.create(name);
      // The server decides where the new card lands in the current query's
      // order — refetch rather than guessing a local splice.
      refresh();
      return created;
    },
    [activeRepository, refresh]
  );

  const deleteCard = useCallback(
    async (cardId: string): Promise<void> => {
      const target = rows.find((row) => row.id === cardId);
      // The reference check is the same tested selector the dialog uses, so the
      // rule cannot drift between the two (threat model, abuse case 3).
      if (!target || !selectIsDeletable(target)) {
        return;
      }
      try {
        await activeRepository.remove(cardId);
        // Refetch the live query; the effect's clamp walks back a page that
        // just lost its last row.
        refresh();
      } catch {
        // Leave the listing as it was; the card is still there to retry.
      }
    },
    [activeRepository, rows, refresh]
  );

  // Only the settled answer may say "nothing matches" — while a query is in
  // flight the skeleton speaks, not a premature empty-state.
  const hasNoSearchMatches = query.search !== '' && total === 0 && !isLoading;

  const value = useMemo<ProductCardsContextValue>(
    () => ({
      rows,
      total,
      isLoading,
      searchTerm,
      isSearchPending,
      setSearchTerm,
      showCount,
      setShowCount,
      page,
      setPage,
      sortState,
      setSort,
      hasNoSearchMatches,
      createCard,
      deleteCard,
      refresh,
    }),
    [
      rows,
      total,
      isLoading,
      searchTerm,
      isSearchPending,
      setSearchTerm,
      showCount,
      setShowCount,
      page,
      setPage,
      sortState,
      setSort,
      hasNoSearchMatches,
      createCard,
      deleteCard,
      refresh,
    ]
  );

  return <ProductCardsContext.Provider value={value}>{children}</ProductCardsContext.Provider>;
};

export const useProductCards = (): ProductCardsContextValue => {
  const context = useContext(ProductCardsContext);
  if (!context) {
    throw new Error('useProductCards must be used inside a ProductCardsProvider');
  }
  return context;
};
